import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { X, User, Phone, Loader2 } from 'lucide-react'
import { savePatient } from '../../services/patientService'
import { formatSriLankanMobile } from '../../lib/utils'

const envUserId = import.meta.env.VITE_USER_ID
const userId = envUserId ? parseInt(envUserId, 10) : null

function SignupModal({ open, onClose, verifiedMobile, onSuccess }) {
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [signupForm, setSignupForm] = useState({
    title: '',
    firstName: '',
    lastName: '',
    nic: '',
    dateOfBirth: '',
    gender: '',
    bloodGroup: '',
    address: '',
    occupation: '',
    maritalStatus: '',
  })

  if (!open || typeof document === 'undefined') return null

  const handleChange = (e) => {
    setSignupForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  const handleSubmit = async () => {
    // Validation
    if (!signupForm.title || !signupForm.firstName || !signupForm.lastName || !signupForm.dateOfBirth || !signupForm.gender) {
      setErrorMessage('Please fill in all required fields.')
      return
    }

    if (!userId) {
      setErrorMessage('Missing user configuration (VITE_USER_ID).')
      return
    }

    setLoading(true)
    setErrorMessage('')

    try {
      const response = await savePatient({
        UserId: userId,
        Title: signupForm.title,
        FirstName: signupForm.firstName,
        LastName: signupForm.lastName,
        Mobile: formatSriLankanMobile(verifiedMobile),
        Gender: signupForm.gender,
        DateOfBirth: signupForm.dateOfBirth,
        NIC: signupForm.nic,
        BloodGroup: signupForm.bloodGroup,
        Occupation: signupForm.occupation,
        MaritalStatus: signupForm.maritalStatus,
        Address: signupForm.address,
      })

      if (response.success) {
        onSuccess?.(response.data)
        onClose?.()
      } else {
        setErrorMessage(response.message || 'Failed to create profile. Please try again.')
      }
    } catch (error) {
      console.error('Signup failed:', error)
      setErrorMessage('An unexpected error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const gradientBg = 'bg-gradient-to-r from-[#7096F3] via-[#4181F3] via-40% to-[#C08EDB]'

  return createPortal(
    <div
      className="fixed inset-0 z-[9997] flex items-center justify-center bg-slate-950/70 px-3 py-3 backdrop-blur-md"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.()
      }}
    >
      <div className="relative w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <header className={`sticky top-0 z-10 flex items-center justify-between px-6 py-2 text-white ${gradientBg}`}>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/80 -mb-0.5">Medica</p>
            <p className="text-xl font-bold">Create Profile</p>
          </div>
          <button
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition"
            aria-label="Close signup modal"
          >
            <X size={18} />
          </button>
        </header>

        {/* Content */}
        <div className="px-6 py-6 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-900">
              Complete your profile to continue. Your mobile number is verified.
            </p>
          </div>

          {/* Mobile Number (Disabled) */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Mobile Number
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                value={formatSriLankanMobile(verifiedMobile)}
                disabled
                className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-slate-200 bg-slate-50 text-slate-600 text-sm font-medium cursor-not-allowed"
              />
            </div>
          </div>

          {/* Title, First Name, Last Name */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <select
                name="title"
                value={signupForm.title}
                onChange={handleChange}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
              >
                <option value="">Select</option>
                <option value="Mr.">Mr.</option>
                <option value="Mrs.">Mrs.</option>
                <option value="Ms.">Ms.</option>
                <option value="Dr.">Dr.</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={signupForm.firstName}
                onChange={handleChange}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                placeholder="John"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="lastName"
                value={signupForm.lastName}
                onChange={handleChange}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                placeholder="Doe"
              />
            </div>
          </div>

          {/* NIC, Date of Birth, Gender */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">NIC</label>
              <input
                type="text"
                name="nic"
                value={signupForm.nic}
                onChange={handleChange}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                placeholder="123456789V"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Date of Birth <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="dateOfBirth"
                value={signupForm.dateOfBirth}
                onChange={handleChange}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Gender <span className="text-red-500">*</span>
              </label>
              <select
                name="gender"
                value={signupForm.gender}
                onChange={handleChange}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
              >
                <option value="">Select</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          {/* Blood Group, Occupation, Marital Status */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Blood Group</label>
              <select
                name="bloodGroup"
                value={signupForm.bloodGroup}
                onChange={handleChange}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
              >
                <option value="">Select</option>
                <option value="O+">O+</option>
                <option value="O-">O-</option>
                <option value="A+">A+</option>
                <option value="A-">A-</option>
                <option value="B+">B+</option>
                <option value="B-">B-</option>
                <option value="AB+">AB+</option>
                <option value="AB-">AB-</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Occupation</label>
              <input
                type="text"
                name="occupation"
                value={signupForm.occupation}
                onChange={handleChange}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                placeholder="e.g., Student"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Marital Status</label>
              <select
                name="maritalStatus"
                value={signupForm.maritalStatus}
                onChange={handleChange}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
              >
                <option value="">Select</option>
                <option value="1">Single</option>
                <option value="2">Married</option>
                <option value="3">Divorced</option>
                <option value="4">Widowed</option>
              </select>
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Address</label>
            <textarea
              name="address"
              value={signupForm.address}
              onChange={handleChange}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
              rows={2}
              placeholder="Enter your address"
            />
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg px-3 py-2 text-sm">
              {errorMessage}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-1">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-2 rounded-xl border-2 border-slate-300 text-slate-700 font-semibold hover:bg-slate-50 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !signupForm.title || !signupForm.firstName || !signupForm.lastName || !signupForm.dateOfBirth || !signupForm.gender}
              className={`flex-1 px-6 py-2 rounded-xl font-semibold text-white ${gradientBg} hover:shadow-lg transition hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="animate-spin" size={16} />
                  Creating...
                </span>
              ) : (
                'Create Profile'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default SignupModal
